import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const ROLES = [["super_admin", "Super Admin"], ["data_entry_operator", "Data Entry Operator"], ["verifier", "Verifier"], ["report_user", "Report User"]];
const empty = { username: "", full_name: "", email: "", role: "data_entry_operator", password: "", is_active: true };
const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/permissions-matrix").then(({ data }) => setMatrix(data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/users/${editing}`, form); toast.success("User updated"); }
      else { await api.post("/users", form); toast.success("User created"); }
      setForm(empty); setEditing(null); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.username}`, { ...u, is_active: !u.is_active, password: null }); toast.success(u.is_active ? "User disabled" : "User enabled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">User Management</h1>

      <form onSubmit={submit} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-5">
        <input data-testid="user-username" required disabled={!!editing} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Username" className={inputCls} />
        <input data-testid="user-fullname" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" className={inputCls} />
        <input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
        <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>{ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input data-testid="user-password" type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "New password (optional)" : "Password"} className={inputCls} />
        <div className="sm:col-span-2 lg:col-span-5 flex gap-2">
          <button data-testid="user-save" className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white">{editing ? "Save User" : "Create User"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Cancel</button>}
        </div>
      </form>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[700px] w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600"><tr>{["Username", "Full Name", "Role", "Status", "Last Login", "Actions"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody data-testid="user-list">
            {users.map((u) => (
              <tr key={u.username} className="border-t border-slate-100">
                <td className="p-3 font-medium">{u.username}</td><td className="p-3">{u.full_name}</td>
                <td className="p-3">{ROLES.find(([v]) => v === u.role)?.[1] || u.role}</td>
                <td className="p-3">{u.is_active ? <span className="text-emerald-700">Active</span> : <span className="text-rose-700">Disabled</span>}</td>
                <td className="p-3 text-xs text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString("en-IN") : "—"}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button data-testid={`user-edit-${u.username}`} onClick={() => { setEditing(u.username); setForm({ ...u, password: "" }); }} className="text-[#0f2545] underline">Edit</button>
                    <button data-testid={`user-toggle-${u.username}`} onClick={() => toggleActive(u)} className="text-rose-600 underline">{u.is_active ? "Disable" : "Enable"}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-base font-semibold text-[#0f2545] md:text-lg">Permission Matrix</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <tbody>
              {Object.entries(matrix).map(([role, perms]) => (
                <tr key={role} className="border-t border-slate-100">
                  <td className="p-2 font-medium">{ROLES.find(([v]) => v === role)?.[1] || role}</td>
                  <td className="p-2 text-slate-600">{perms.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
